import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getQuotes } from '@/lib/api';
import { 
  FileText, 
  ArrowRight, 
  Clock,
  Building2,
  Plus
} from 'lucide-react';

const QuoteList = () => {
  const { isUSA } = useLocale();
  const [receivedQuotes, setReceivedQuotes] = useState([]);
  const [sentQuotes, setSentQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const statusLabels = isUSA ? {
    nueva: 'New',
    en_revision: 'Under review',
    respondida: 'Responded',
    negociacion: 'Negotiation',
    cerrada: 'Closed',
    cancelada: 'Cancelled'
  } : {
    nueva: 'Nueva',
    en_revision: 'En revisión',
    respondida: 'Respondida',
    negociacion: 'Negociación',
    cerrada: 'Cerrada',
    cancelada: 'Cancelada'
  };

  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        const [received, sent] = await Promise.all([
          getQuotes('received'),
          getQuotes('sent')
        ]);
        setReceivedQuotes(received);
        setSentQuotes(sent);
      } catch (error) {
        console.error('Error fetching quotes:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuotes();
  }, []);

  const QuoteCard = ({ quote, type }) => (
    <Card className="card-industrial hover-lift" data-testid={`quote-card-${quote.quote_id}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-slate-400" />
              <span className="font-medium text-slate-900">
                {type === 'received' 
                  ? quote.requester_company_name 
                  : (isUSA ? 'Multiple suppliers' : 'Múltiples proveedores')}
              </span>
            </div>
            <h3 className="font-semibold text-slate-900">{quote.product_type}</h3>
            <p className="text-sm text-slate-500 mt-1">
              {isUSA ? 'Quantity' : 'Cantidad'}: {quote.quantity.toLocaleString()} {isUSA ? 'units' : 'unidades'}
            </p>
            {quote.delivery_city && (
              <p className="text-sm text-slate-500">
                {isUSA ? 'Delivery to' : 'Entrega en'}: {quote.delivery_city}
              </p>
            )}
          </div>
          <Badge className={`status-${quote.status}`}>
            {statusLabels[quote.status] || quote.status}
          </Badge>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <Clock className="w-3 h-3" />
            {new Date(quote.created_at).toLocaleDateString(isUSA ? 'en-US' : 'es-MX')}
          </div>
          <Link to={`/dashboard/quotes/${quote.quote_id}`}>
            <Button variant="ghost" size="sm">
              {isUSA ? 'View details' : 'Ver detalles'}
              <ArrowRight className="ml-1 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="quotes-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="page-title">{isUSA ? 'Quotes' : 'Cotizaciones'}</h1>
          <p className="page-subtitle">{isUSA ? 'Manage quote requests' : 'Gestiona solicitudes de cotización'}</p>
        </div>
        <Link to="/dashboard/quotes/new">
          <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-quote-btn">
            <Plus className="mr-2 w-4 h-4" />
            {isUSA ? 'New quote' : 'Nueva cotización'}
          </Button>
        </Link>
      </div>

      <Tabs defaultValue="received">
        <TabsList>
          <TabsTrigger value="received" data-testid="received-tab">
            {isUSA ? 'Received' : 'Recibidas'} ({receivedQuotes.length})
          </TabsTrigger>
          <TabsTrigger value="sent" data-testid="sent-tab">
            {isUSA ? 'Sent' : 'Enviadas'} ({sentQuotes.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="received" className="mt-6">
          {receivedQuotes.length === 0 ? (
            <div className="empty-state">
              <FileText className="empty-state-icon" />
              <h3 className="empty-state-title">
                {isUSA ? 'No quotes received' : 'No hay cotizaciones recibidas'}
              </h3>
              <p className="empty-state-text">
                {isUSA 
                  ? 'Quotes you receive from manufacturers will appear here'
                  : 'Las cotizaciones que recibas de fabricantes aparecerán aquí'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {receivedQuotes.map((quote) => (
                <QuoteCard key={quote.quote_id} quote={quote} type="received" />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="sent" className="mt-6">
          {sentQuotes.length === 0 ? (
            <div className="empty-state">
              <FileText className="empty-state-icon" />
              <h3 className="empty-state-title">
                {isUSA ? "You haven't sent any quotes" : 'No has enviado cotizaciones'}
              </h3>
              <p className="empty-state-text">
                {isUSA 
                  ? 'Search for suppliers and request quotes'
                  : 'Busca proveedores y solicita cotizaciones'}
              </p>
              <Link to="/search">
                <Button className="mt-4 bg-emerald-600 hover:bg-emerald-700">
                  {isUSA ? 'Search suppliers' : 'Buscar proveedores'}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sentQuotes.map((quote) => (
                <QuoteCard key={quote.quote_id} quote={quote} type="sent" />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default QuoteList;
